import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import {
  FilterService,
  SinglePopupService,
  PopupCloseEvent,
} from '@progress/kendo-angular-grid';
import { PopupSettings } from '@progress/kendo-angular-dateinputs';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { MaskService } from '../../services/mask.service';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { CommonModule } from '@angular/common';
import { MaskitoDirective } from '@maskito/angular';
import { FormsModule } from '@angular/forms';

const closest = (
  node: HTMLElement,
  predicate: (node: HTMLElement) => boolean
): HTMLElement => {
  while (node && !predicate(node)) {
    node = node.parentNode as HTMLElement;
  }

  return node;
};

@Component({
  selector: 'app-amount-range-filter',
  templateUrl: './amount-range-filter.component.html',
  imports: [
    KENDO_DROPDOWNLIST,
    KENDO_LABEL,
    KENDO_TEXTBOX,
    CommonModule,
    MaskitoDirective,
    FormsModule,
  ],
})
export class AmountRangeFilterComponent implements OnInit, OnDestroy {
  private element = inject(ElementRef);
  private popupService = inject(SinglePopupService);
  private mask = inject(MaskService);

  @Input() public filter: CompositeFilterDescriptor;
  @Input() public filterService: FilterService;
  @Input() public field: string;
  @Output() updateFilter = new EventEmitter();

  public equalGreater;
  public equalLess;
  public selectedOperation;
  dollarMask = this.mask.dollarMaskSpecs();

  operationList = [
    { display: 'Greater Than', value: 1 },
    { display: 'Less Than', value: 2 },
    { display: 'Between', value: 3 },
  ];

  public popupSettings: PopupSettings = {
    popupClass: 'amount-range-filter',
  };

  private popupSubscription: Subscription;

  constructor() {
    const popupService = this.popupService;

    // Handle the service onClose event and prevent the menu from closing when the datepickers are still active.
    this.popupSubscription = popupService.onClose.subscribe(
      (e: PopupCloseEvent) => {
        if (
          document.activeElement &&
          closest(
            document.activeElement as HTMLElement,
            node =>
              node === this.element.nativeElement ||
              String(node.className).indexOf('amount-range-filter') >= 0
          )
        ) {
          e.preventDefault();
        }
      }
    );
  }

  public ngOnInit(): void {
    this.findOp();
    this.equalGreater = this.findValue('gte');
    this.equalLess = this.findValue('lte');
  }

  public ngOnDestroy(): void {
    this.popupSubscription.unsubscribe();
  }

  public onGreaterChange(value: string): void {
    this.filterRange(value, this.equalLess);
  }

  public onLessChange(value: string): void {
    this.filterRange(this.equalGreater, value);
  }

  private findValue(operator) {
    const filter = this.filter.filters.filter(
      x =>
        (x as FilterDescriptor).field === this.field &&
        (x as FilterDescriptor).operator === operator
    )[0];
    return filter
      ? this.mask.applyMoneyMask((filter as FilterDescriptor).value)
      : null;
  }

  private findOp() {
    this.selectedOperation = {};
    this.filter.filters.forEach(x => {
      if ((x as FilterDescriptor).field === this.field) {
        if ((x as FilterDescriptor).operator === 'gte') {
          if (Object.values(this.selectedOperation).includes(2)) {
            this.selectedOperation = { display: 'Between', value: 3 };
          } else {
            this.selectedOperation = { display: 'Greater Than', value: 1 };
          }
        } else if ((x as FilterDescriptor).operator === 'lte') {
          if (Object.values(this.selectedOperation).includes(1)) {
            this.selectedOperation = { display: 'Between', value: 3 };
          } else {
            this.selectedOperation = { display: 'Less Than', value: 2 };
          }
        }
      }
    });
    if (!this.selectedOperation) {
      this.selectedOperation = { display: 'Greater Than', value: 1 };
    }
  }

  private filterRange(equalGreater, equalLess) {
    const filters = [];

    if (Object.values(this.selectedOperation).includes(1)) {
      this.equalLess = null;
      equalLess = null;
    } else if (Object.values(this.selectedOperation).includes(2)) {
      this.equalGreater = null;
      equalGreater = null;
    }

    if (equalGreater) {
      filters.push({
        field: this.field,
        operator: 'gte',
        value: this.mask.removeMoneyMask(equalGreater),
      });
      this.equalGreater = equalGreater;
    }

    if (equalLess) {
      filters.push({
        field: this.field,
        operator: 'lte',
        value: this.mask.removeMoneyMask(equalLess),
      });
      this.equalLess = equalLess;
    }

    this.filterService.filter({
      logic: 'and',
      filters: filters,
    });
    this.updateFilter.emit(this.field);
  }
}
